import React, { useState, useEffect } from "react";
import {
  Box, Button, FormControl, FormLabel, Input,
  VStack, Text, Heading, Alert, AlertIcon, useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const B2CResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const toast = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    if (!token) setTokenError(true);
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords do not match", status: "error", duration: 3000 });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: "Password must be at least 8 characters", status: "error", duration: 3000 });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${apiUrl}/b2c/recover-password`, {
        token,
        new_password: newPassword,
      });
      setIsSuccess(true);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("invalid") || detail?.includes("expired")) {
        setTokenError(true);
      } else {
        toast({
          title: detail ?? "Failed to reset password. Please try again.",
          status: "error",
          duration: 4000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box maxW="420px" mx="auto" mt={16} p={8}
      borderWidth="1px" borderRadius="xl" boxShadow="lg">

      {tokenError ? (
        <VStack spacing={4}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            This reset link is invalid or has expired. Please request a new one.
          </Alert>
          <Button
            colorScheme="purple"
            width="100%"
            onClick={() => navigate("/practice/login")}
          >
            Back to Login
          </Button>
        </VStack>

      ) : isSuccess ? (
        <VStack spacing={4}>
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            Your password has been reset successfully.
          </Alert>
          <Text fontSize="sm" color="gray.500" textAlign="center">
            You can now sign in with your new password.
          </Text>
          <Button
            colorScheme="purple"
            width="100%"
            onClick={() => navigate("/practice/login")}
          >
            Sign In
          </Button>
        </VStack>

      ) : (
        <>
          <VStack spacing={1} mb={6} align="start">
            <Heading size="lg">Set New Password</Heading>
            <Text color="gray.500" fontSize="sm">
              Choose a strong password for your account.
            </Text>
          </VStack>

          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">New Password</FormLabel>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Confirm New Password</FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your new password"
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="purple"
                width="100%"
                isLoading={isLoading}
                loadingText="Updating..."
              >
                Reset Password
              </Button>
            </VStack>
          </form>
        </>
      )}
    </Box>
  );
};

export default B2CResetPassword;